import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Route, Loader2 } from "lucide-react";

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 py-2 cursor-pointer">
    <span className="text-sm text-slate-600">{label}</span>
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? "bg-cobalt" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform left-0.5 ${checked ? "translate-x-[16px]" : ""}`} />
    </button>
  </label>
);

export default function AdminRoutingConfig() {
  const [cfg, setCfg] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    api.get("/routing/admin/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/routing/admin/events?limit=25").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/routing/admin/reservations?limit=25").then(({ data }) => setReservations(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/routing/admin/config", { ...cfg, reason });
      setCfg(data);
      toast.success("Routing configuration saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  return (
    <div className="space-y-6" data-testid="admin-routing-config">
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2 mb-4">
          <Route className="w-5 h-5 text-cobalt" />
          <h3 className="font-heading font-semibold text-navy">Attorney Routing Rules</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-x-8">
          <Toggle label="Routing enabled" checked={cfg.enabled} onChange={(v) => set("enabled", v)} testid="rc-enabled" />
          <Toggle label="Rear-End Priority workflow enabled" checked={cfg.rear_end_enabled} onChange={(v) => set("rear_end_enabled", v)} testid="rc-rear-end" />
          <Toggle label="Require verified other-driver insurance" checked={cfg.require_other_driver_insurance} onChange={(v) => set("require_other_driver_insurance", v)} testid="rc-insurance" />
          <Toggle label="Testing mode (allow test accounts)" checked={cfg.testing_mode} onChange={(v) => set("testing_mode", v)} testid="rc-testing" />
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Minimum Trust Score</label>
            <input type="number" min="0" max="100" value={cfg.min_trust_score} data-testid="rc-min-trust"
              onChange={(e) => set("min_trust_score", Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Reservation minutes</label>
            <input type="number" min="1" max="120" value={cfg.reservation_minutes} data-testid="rc-reservation-min"
              onChange={(e) => set("reservation_minutes", Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Max priority offers before marketplace</label>
            <input type="number" min="1" max="20" value={cfg.max_priority_offers} data-testid="rc-max-offers"
              onChange={(e) => set("max_priority_offers", Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-slate-500">Fair-routing method</label>
          <select value={cfg.fair_method} onChange={(e) => set("fair_method", e.target.value)} data-testid="rc-fair-method"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="round_robin">Round robin</option>
            <option value="weighted_rotation">Weighted rotation</option>
            <option value="geography">Geography match</option>
            <option value="response_history">Response history</option>
          </select>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 w-full">
            <label className="text-xs font-medium text-slate-500">Reason for change (recorded in the audit trail)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rc-reason"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Why are you changing routing rules?" />
          </div>
          <button onClick={save} disabled={saving} data-testid="rc-save"
            className="px-6 py-2.5 rounded-full bg-navy text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">{saving ? "Saving…" : "Save routing rules"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Recent reservations</h3>
          {reservations.length === 0 ? <p className="text-sm text-slate-400">No reservations yet.</p> :
            reservations.map((r) => (
              <div key={r.reservation_id} className="py-2 border-b border-slate-50 last:border-0 text-xs flex justify-between gap-2">
                <span className="text-slate-600">{r.case_number} → {r.attorney_name}</span>
                <span className={`font-semibold ${r.status === "accepted" ? "text-emerald-600" : r.status === "active" ? "text-cobalt" : "text-slate-400"}`}>{r.status}</span>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-3xl border border-slate-100 p-6">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Routing audit trail</h3>
          {events.length === 0 ? <p className="text-sm text-slate-400">No routing events yet.</p> :
            events.map((e) => (
              <div key={e.event_id} className="py-2 border-b border-slate-50 last:border-0 text-xs">
                <span className="font-semibold text-navy">{e.event}</span>
                <span className="text-slate-400"> · {e.case_id || "config"} · {new Date(e.at).toLocaleString()}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
